// ── Form Builder Types ──

export interface FormBlockValidation {
  minLength?: number
  maxLength?: number
  min?: number
  max?: number
  pattern?: string
  accept?: string // for file inputs
  maxFileSize?: number // in MB
  options?: { label: string; value: string }[] // for select, radio, checkbox
}

export interface FormBlock {
  id: string
  type: FormBlockType
  label: string
  name: string
  placeholder: string
  required: boolean
  helpText: string
  defaultValue: string
  width: 'full' | 'half'
  validation: FormBlockValidation
  // Layout-specific
  content?: string // for heading, paragraph
  level?: 1 | 2 | 3 | 4 // for heading
  height?: number // for spacer (px)
  src?: string // for image
  alt?: string // for image
  buttonText?: string // for submit
  buttonAlign?: 'left' | 'center' | 'right' | 'full'
  buttonColor?: string // custom button background color (overrides theme)
  // Consent-specific
  consentText?: string // label text for single consent checkbox
}

export type FormBlockType =
  | 'text'
  | 'email'
  | 'phone'
  | 'number'
  | 'textarea'
  | 'url'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'consent'
  | 'date'
  | 'file'
  | 'hidden'
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'spacer'
  | 'image'
  | 'submit'

export interface FormTheme {
  fontFamily: string
  primaryColor: string
  borderRadius: number
  backgroundColor: string
  labelColor: string
  inputBorderColor: string
  buttonStyle: 'filled' | 'outline'
  // Enhanced styling
  backgroundType: 'solid' | 'gradient'
  gradientFrom: string
  gradientTo: string
  gradientDirection:
    | 'to bottom'
    | 'to right'
    | 'to bottom right'
    | 'to top right'
  labelFontSize: number
  labelFontWeight: '400' | '500' | '600' | '700'
  inputBackground: string
  inputFocusColor: string
  inputShadow: boolean
  formPadding: number
  fieldSpacing: number
  buttonTextColor: string
  buttonShadow: boolean
  containerShadow: 'none' | 'sm' | 'md' | 'lg'
  containerBorder: boolean
  headingColor: string
  paragraphColor: string
}

export const DEFAULT_FORM_THEME: FormTheme = {
  fontFamily: 'Inter, system-ui, sans-serif',
  primaryColor: '#2563eb',
  borderRadius: 6,
  backgroundColor: '#ffffff',
  labelColor: '#374151',
  inputBorderColor: '#d1d5db',
  buttonStyle: 'filled',
  backgroundType: 'solid',
  gradientFrom: '#667eea',
  gradientTo: '#764ba2',
  gradientDirection: 'to bottom right',
  labelFontSize: 14,
  labelFontWeight: '500',
  inputBackground: '#ffffff',
  inputFocusColor: '#2563eb',
  inputShadow: false,
  formPadding: 32,
  fieldSpacing: 20,
  buttonTextColor: '#ffffff',
  buttonShadow: false,
  containerShadow: 'none',
  containerBorder: false,
  headingColor: '#111827',
  paragraphColor: '#6b7280',
}

export interface StylePreset {
  name: string
  emoji: string
  theme: Partial<FormTheme>
}

export const FORM_STYLE_PRESETS: StylePreset[] = [
  {
    name: 'Clean Default',
    emoji: '✨',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#2563eb',
      borderRadius: 6,
      backgroundColor: '#ffffff',
      backgroundType: 'solid',
      labelColor: '#374151',
      inputBorderColor: '#d1d5db',
      inputBackground: '#ffffff',
      inputShadow: false,
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: false,
      containerShadow: 'none',
      containerBorder: false,
      headingColor: '#111827',
      paragraphColor: '#6b7280',
      formPadding: 32,
      fieldSpacing: 20,
    },
  },
  {
    name: 'Soft Gradient',
    emoji: '🌈',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#7c3aed',
      borderRadius: 12,
      backgroundColor: '#ffffff',
      backgroundType: 'gradient',
      gradientFrom: '#e0c3fc',
      gradientTo: '#8ec5fc',
      gradientDirection: 'to bottom right',
      labelColor: '#4c1d95',
      inputBorderColor: '#c4b5fd',
      inputBackground: 'rgba(255,255,255,0.85)',
      inputShadow: true,
      inputFocusColor: '#7c3aed',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: false,
      headingColor: '#4c1d95',
      paragraphColor: '#6d28d9',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
  {
    name: 'Dark Mode',
    emoji: '🌙',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#6366f1',
      borderRadius: 8,
      backgroundColor: '#1e1e2e',
      backgroundType: 'solid',
      labelColor: '#cbd5e1',
      inputBorderColor: '#334155',
      inputBackground: '#0f172a',
      inputShadow: false,
      inputFocusColor: '#818cf8',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'lg',
      containerBorder: true,
      headingColor: '#f1f5f9',
      paragraphColor: '#94a3b8',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
  {
    name: 'Warm & Friendly',
    emoji: '☀️',
    theme: {
      fontFamily: 'Georgia, serif',
      primaryColor: '#ea580c',
      borderRadius: 10,
      backgroundColor: '#fffbeb',
      backgroundType: 'solid',
      labelColor: '#92400e',
      inputBorderColor: '#fbbf24',
      inputBackground: '#ffffff',
      inputShadow: true,
      inputFocusColor: '#ea580c',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'md',
      containerBorder: false,
      headingColor: '#78350f',
      paragraphColor: '#a16207',
      formPadding: 36,
      fieldSpacing: 22,
    },
  },
  {
    name: 'Minimal Outline',
    emoji: '🔲',
    theme: {
      fontFamily: 'Inter, system-ui, sans-serif',
      primaryColor: '#111827',
      borderRadius: 0,
      backgroundColor: '#ffffff',
      backgroundType: 'solid',
      labelColor: '#111827',
      labelFontWeight: '600',
      inputBorderColor: '#111827',
      inputBackground: '#ffffff',
      inputShadow: false,
      inputFocusColor: '#111827',
      buttonStyle: 'outline',
      buttonTextColor: '#111827',
      buttonShadow: false,
      containerShadow: 'none',
      containerBorder: true,
      headingColor: '#111827',
      paragraphColor: '#4b5563',
      formPadding: 32,
      fieldSpacing: 20,
    },
  },
  {
    name: 'Ocean Breeze',
    emoji: '🌊',
    theme: {
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      primaryColor: '#0891b2',
      borderRadius: 12,
      backgroundColor: '#ffffff',
      backgroundType: 'gradient',
      gradientFrom: '#cffafe',
      gradientTo: '#a5f3fc',
      gradientDirection: 'to bottom',
      labelColor: '#164e63',
      inputBorderColor: '#67e8f9',
      inputBackground: 'rgba(255,255,255,0.9)',
      inputShadow: true,
      inputFocusColor: '#0891b2',
      buttonStyle: 'filled',
      buttonTextColor: '#ffffff',
      buttonShadow: true,
      containerShadow: 'md',
      containerBorder: false,
      headingColor: '#155e75',
      paragraphColor: '#0e7490',
      formPadding: 40,
      fieldSpacing: 24,
    },
  },
]

export interface FormBuilderSchema {
  blocks: FormBlock[]
  theme: FormTheme
}

// ── Email Builder Types ──

export type EmailBlockType =
  | 'text'
  | 'heading'
  | 'button'
  | 'image'
  | 'video_thumbnail'
  | 'divider'
  | 'spacer'
  | 'columns'
  | 'social'

export interface EmailBlock {
  id: string
  type: EmailBlockType
  // Text / Heading
  content?: string
  fontSize?: number
  fontWeight?: string
  color?: string
  align?: 'left' | 'center' | 'right'
  lineHeight?: number
  // Button
  buttonText?: string
  buttonUrl?: string
  buttonBgColor?: string
  buttonTextColor?: string
  buttonBorderRadius?: number
  buttonWidth?: 'auto' | 'full'
  // Image / Video
  src?: string
  alt?: string
  imageWidth?: number // percentage
  href?: string
  // Video thumbnail
  videoUrl?: string
  // Divider
  dividerColor?: string
  dividerThickness?: number
  dividerStyle?: 'solid' | 'dashed' | 'dotted'
  // Spacer
  height?: number
  // Columns
  columns?: EmailBlock[][]
  columnRatio?: '50-50' | '33-67' | '67-33' | '33-33-33'
  // Social
  socialLinks?: { platform: string; url: string }[]
  socialIconSize?: number
  // Shared
  paddingTop?: number
  paddingBottom?: number
  paddingLeft?: number
  paddingRight?: number
  backgroundColor?: string
}

export interface EmailBuilderSchema {
  blocks: EmailBlock[]
  globalStyles: EmailGlobalStyles
}

export interface EmailGlobalStyles {
  backgroundColor: string
  contentWidth: number
  fontFamily: string
  textColor: string
  linkColor: string
}

export const DEFAULT_EMAIL_STYLES: EmailGlobalStyles = {
  backgroundColor: '#f3f4f6',
  contentWidth: 600,
  fontFamily: 'Arial, Helvetica, sans-serif',
  textColor: '#333333',
  linkColor: '#2563eb',
}

// ── Block Palette Definitions ──

export interface PaletteItem {
  type: FormBlockType | EmailBlockType
  label: string
  icon: string // lucide icon name
  category: string
}

export const FORM_PALETTE: PaletteItem[] = [
  { type: 'text', label: 'Text Input', icon: 'Type', category: 'Basic' },
  { type: 'email', label: 'Email', icon: 'Mail', category: 'Basic' },
  { type: 'phone', label: 'Phone', icon: 'Phone', category: 'Basic' },
  { type: 'number', label: 'Number', icon: 'Hash', category: 'Basic' },
  {
    type: 'textarea',
    label: 'Text Area',
    icon: 'AlignLeft',
    category: 'Basic',
  },
  {
    type: 'select',
    label: 'Dropdown',
    icon: 'ChevronDown',
    category: 'Selection',
  },
  {
    type: 'radio',
    label: 'Radio Group',
    icon: 'Circle',
    category: 'Selection',
  },
  {
    type: 'checkbox',
    label: 'Checkboxes',
    icon: 'CheckSquare',
    category: 'Selection',
  },
  {
    type: 'consent',
    label: 'Consent / Opt-in',
    icon: 'SquareCheck',
    category: 'Selection',
  },
  { type: 'date', label: 'Date', icon: 'Calendar', category: 'Advanced' },
  { type: 'file', label: 'File Upload', icon: 'Upload', category: 'Advanced' },
  { type: 'url', label: 'URL', icon: 'Link', category: 'Advanced' },
  {
    type: 'hidden',
    label: 'Hidden Field',
    icon: 'EyeOff',
    category: 'Advanced',
  },
  { type: 'heading', label: 'Heading', icon: 'Heading', category: 'Layout' },
  {
    type: 'paragraph',
    label: 'Paragraph',
    icon: 'FileText',
    category: 'Layout',
  },
  { type: 'divider', label: 'Divider', icon: 'Minus', category: 'Layout' },
  { type: 'spacer', label: 'Spacer', icon: 'MoveVertical', category: 'Layout' },
  { type: 'image', label: 'Image', icon: 'Image', category: 'Layout' },
  { type: 'submit', label: 'Submit Button', icon: 'Send', category: 'Action' },
]

export const EMAIL_PALETTE: PaletteItem[] = [
  { type: 'text', label: 'Text', icon: 'Type', category: 'Content' },
  { type: 'heading', label: 'Heading', icon: 'Heading', category: 'Content' },
  {
    type: 'button',
    label: 'Button',
    icon: 'MousePointer',
    category: 'Content',
  },
  { type: 'image', label: 'Image', icon: 'Image', category: 'Media' },
  { type: 'video_thumbnail', label: 'Video', icon: 'Play', category: 'Media' },
  { type: 'divider', label: 'Divider', icon: 'Minus', category: 'Layout' },
  { type: 'spacer', label: 'Spacer', icon: 'MoveVertical', category: 'Layout' },
  { type: 'columns', label: 'Columns', icon: 'Columns', category: 'Layout' },
  { type: 'social', label: 'Social Icons', icon: 'Share2', category: 'Social' },
]

// ── Helpers ──

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

export function labelToName(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '') || 'field'
  )
}

export function createFormBlock(type: FormBlockType): FormBlock {
  const id = generateId()
  const defaults: Record<string, Partial<FormBlock>> = {
    text: { label: 'Text Field', placeholder: 'Enter text...' },
    email: {
      label: 'Email Address',
      placeholder: 'you@example.com',
      name: 'email',
    },
    phone: {
      label: 'Phone Number',
      placeholder: '+1 (555) 000-0000',
      name: 'phone',
    },
    number: { label: 'Number', placeholder: '0' },
    textarea: { label: 'Message', placeholder: 'Type your message...' },
    url: { label: 'Website URL', placeholder: 'https://' },
    select: {
      label: 'Select Option',
      validation: {
        options: [
          { label: 'Option 1', value: 'option_1' },
          { label: 'Option 2', value: 'option_2' },
        ],
      },
    },
    radio: {
      label: 'Choose One',
      validation: {
        options: [
          { label: 'Option A', value: 'a' },
          { label: 'Option B', value: 'b' },
        ],
      },
    },
    checkbox: {
      label: 'Select All That Apply',
      validation: {
        options: [
          { label: 'Choice 1', value: '1' },
          { label: 'Choice 2', value: '2' },
        ],
      },
    },
    consent: {
      label: 'Consent',
      name: 'consent',
      consentText: 'I agree to receive updates and communications',
    },
    date: { label: 'Date' },
    file: {
      label: 'Upload File',
      validation: { accept: '.pdf,.doc,.docx,.jpg,.png', maxFileSize: 5 },
    },
    hidden: { label: 'Hidden Field', name: 'hidden_field', defaultValue: '' },
    heading: { label: '', content: 'Form Heading', level: 2 },
    paragraph: { label: '', content: 'Add your description text here.' },
    divider: { label: '' },
    spacer: { label: '', height: 24 },
    image: { label: '', src: '', alt: 'Image' },
    submit: { label: '', buttonText: 'Submit', buttonAlign: 'full' },
  }
  const d = defaults[type] || {}
  return {
    id,
    type,
    label: d.label ?? 'Field',
    name: d.name ?? labelToName(d.label || 'field') + '_' + id.slice(0, 4),
    placeholder: d.placeholder ?? '',
    required: false,
    helpText: '',
    defaultValue: d.defaultValue ?? '',
    width: 'full',
    validation: d.validation ?? {},
    content: d.content,
    level: d.level,
    height: d.height,
    src: d.src,
    alt: d.alt,
    buttonText: d.buttonText,
    buttonAlign: d.buttonAlign,
    consentText: d.consentText,
  }
}

export function createEmailBlock(type: EmailBlockType): EmailBlock {
  const id = generateId()
  const defaults: Record<string, Partial<EmailBlock>> = {
    text: {
      content:
        'Add your text here. You can use merge tags like {{First_Name}}.',
      fontSize: 16,
      color: '#333333',
      align: 'left',
      lineHeight: 1.6,
    },
    heading: {
      content: 'Your Heading',
      fontSize: 28,
      fontWeight: 'bold',
      color: '#111827',
      align: 'center',
      lineHeight: 1.3,
    },
    button: {
      buttonText: 'Click Here',
      buttonUrl: '#',
      buttonBgColor: '#2563eb',
      buttonTextColor: '#ffffff',
      buttonBorderRadius: 6,
      buttonWidth: 'auto',
      align: 'center',
    },
    image: { src: '', alt: 'Image', imageWidth: 100, align: 'center' },
    video_thumbnail: {
      src: '',
      alt: 'Watch Video',
      videoUrl: '',
      imageWidth: 100,
      align: 'center',
    },
    divider: {
      dividerColor: '#e5e7eb',
      dividerThickness: 1,
      dividerStyle: 'solid',
    },
    spacer: { height: 20 },
    columns: { columns: [[], []], columnRatio: '50-50' },
    social: {
      socialLinks: [
        { platform: 'facebook', url: '#' },
        { platform: 'twitter', url: '#' },
        { platform: 'instagram', url: '#' },
      ],
      socialIconSize: 32,
      align: 'center',
    },
  }
  const d = defaults[type] || {}
  return {
    id,
    type,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 20,
    paddingRight: 20,
    ...d,
  }
}
